import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ExternalLink, MessageCircle, Heart, Share2, Eye } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useArtworks } from "@/hooks/useArtworks";
import OrderDialog from "@/components/OrderDialog";
import StructuredData from "@/components/StructuredData";
import SEOHead from "@/components/SEOHead";
import FloatingActions from "@/components/FloatingActions";
import { generateArtworkSEO, generateSchemaMarkup } from "@/utils/seo";
import { trackProductView, trackProductAction, trackSocialClick } from "@/lib/analytics";

// Import all artwork images
import elegantnost from "@/assets/elegantnost.jpg";
import tenSkakuna from "@/assets/ten-skakuna.jpg";
import tigr from "@/assets/tigr.jpg";
import koshelekIliZhizn from "@/assets/koshelek-ili-zhizn.jpg";
import nochnoyPolet from "@/assets/nochnoy-polet.jpg";
import vesennySad from "@/assets/vesenny-sad.jpg";
import zmeya from "@/assets/zmeya.jpg";
import kolibri from "@/assets/kolibri.jpg";
import zolotoyLes from "@/assets/zolotoy-les.jpg";
import santaKlaus from "@/assets/santa-klaus.jpg";
import slonicNew from "@/assets/slony-dali-new.jpg";
import slonic from "@/assets/slony-dali.jpg";
import naedine from "@/assets/naedine.jpg";
import fruits from "@/assets/fruits.jpg";
import sestraMiloserdia from "@/assets/sestra-miloserdia.jpg";
import kleopatraSoLvom from "@/assets/kleopatra-so-lvom.jpg";
import peresechenieGranits from "@/assets/peresechenie-granits.jpg";
import peresechenieGranitsSite from "@/assets/peresechenie-granits-site.jpg";
import zolotoyVikhr from "@/assets/zolotoy-vikhr.jpg";
import zolotoyVikhrSite from "@/assets/zolotoy-vikhr-site.jpg";
import zenit from "@/assets/zenit.jpg";
import art001 from "@/assets/art-001.jpg";

// Updated images imports
import autumnForestUpdated from "@/assets/autumn-forest-updated.jpg";
import autumnForest from "@/assets/autumn-forest.jpg";
import belyyParusNew from "@/assets/belyy-parus-new.jpg";
import birthOfStarNew from "@/assets/birth-of-star-new.jpg";
import birthOfStarUpdated from "@/assets/birth-of-star-updated.jpg";
import cosmicEyeUpdated from "@/assets/cosmic-eye-updated.jpg";
import cosmicEye from "@/assets/cosmic-eye.jpg";
import girlInTheDistanceUpdated from "@/assets/girl-in-the-distance-updated.jpg";
import girlInTheDistance from "@/assets/girl-in-the-distance.jpg";
import goluboyNew from "@/assets/goluboy-new.jpg";
import luchSolntsaNew from "@/assets/luch-solntsa-new.jpg";
import luchSolntsaUpdated from "@/assets/luch-solntsa-updated.jpg";
import matritcaNew from "@/assets/matritsa-new.jpg";
import lavandovyePolyaNew from "@/assets/lavandovye-polya-new.jpg";
import ostrovNew from "@/assets/ostrov-new.jpg";
import mediterraneanUpdated from "@/assets/mediterranean-updated.jpg";
import treeOnTheHillUpdated from "@/assets/tree-on-the-hill-updated.jpg";
import wheatFieldsUpdated from "@/assets/wheat-fields-updated.jpg";
import winterLandscapeUpdated from "@/assets/winter-landscape-updated.jpg";
import winterUpdated from "@/assets/winter-updated.jpg";
import vetreniDubrava from "@/assets/vetrenichka-dubravnaya.jpg";

// Image mapping
const imageMap: { [key: string]: string } = {
  elegantnost,
  "ten-skakuna": tenSkakuna,
  tigr,
  "koshelek-ili-zhizn": koshelekIliZhizn,
  "nochnoy-polet": nochnoyPolet,
  "vesenny-sad": vesennySad,
  zmeya,
  kolibri,
  "zolotoy-les": zolotoyLes,
  "santa-klaus": santaKlaus,
  "slony-dali-new": slonicNew,
  "slony-dali": slonic,
  naedine,
  fruits,
  "sestra-miloserdia": sestraMiloserdia,
  "kleopatra-so-lvom": kleopatraSoLvom,
  "peresechenie-granits": peresechenieGranits,
  "peresechenie-granits-site": peresechenieGranitsSite,
  "zolotoy-vikhr": zolotoyVikhr,
  "zolotoy-vikhr-site": zolotoyVikhrSite,
  zenit,
  // Main artwork images
  "autumn-forest": autumnForest,
  "autumn-forest-updated": autumnForestUpdated,
  "belyy-parus-new": belyyParusNew,
  "bely-parus": belyyParusNew,
  "birth-of-star-new": birthOfStarNew,
  "birth-of-star-updated": birthOfStarUpdated,
  "rozhdenie-zvezdy": birthOfStarNew,
  "cosmic-eye": cosmicEye,
  "cosmic-eye-updated": cosmicEyeUpdated,
  "girl-in-the-distance": girlInTheDistance,
  "girl-in-the-distance-updated": girlInTheDistanceUpdated,
  "goluboy": goluboyNew,
  "goluboy-new": goluboyNew,
  "luch-solntsa": luchSolntsaUpdated,
  "luch-solntsa-new": luchSolntsaNew,
  "luch-solntsa-updated": luchSolntsaUpdated,
  "matritsa": matritcaNew,
  "matritsa-new": matritcaNew,
  "lavandovye-polya": lavandovyePolyaNew,
  "lavandovye-polya-new": lavandovyePolyaNew,
  "ostrov": ostrovNew,
  "ostrov-new": ostrovNew,
  "mediterranean": mediterraneanUpdated,
  "mediterranean-updated": mediterraneanUpdated,
  "tree-on-the-hill": treeOnTheHillUpdated,
  "tree-on-the-hill-updated": treeOnTheHillUpdated,
  "wheat-fields": wheatFieldsUpdated,
  "wheat-fields-updated": wheatFieldsUpdated,
  "winter-landscape": winterLandscapeUpdated,
  "winter-landscape-updated": winterLandscapeUpdated,
  "winter": winterUpdated,
  "winter-updated": winterUpdated,
  "vetrenichka-dubravnaya": vetreniDubrava
};

const ArtworkDetail = () => {
  const { '*': fullPath } = useParams();
  const { toast } = useToast();
  const { artworks, loading } = useArtworks();
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  
  // Находим работу по website_link (ПРИОРИТЕТ) или по старому id
  const allArtworks = [...artworks.featured, ...artworks.gallery];
  const artwork = allArtworks.find(art => {
    // Очищаем fullPath от начального и конечного слеша
    const cleanFullPath = fullPath?.replace(/^\//, '').replace(/\/$/, '') || '';
    
    console.log('🔍 Поиск товара:', {
      fullPath,
      cleanFullPath,
      artTitle: art.title,
      websiteLink: art.website_link
    });
    
    // ПРИОРИТЕТ 1: Ищем по website_link из БД
    if (art.website_link && art.website_link.trim() !== '') {
      // Удаляем домен и протокол, оставляем только путь
      const fullArtworkPath = art.website_link
        .replace(/^https?:\/\/[^\/]+/, '')  // Убираем https://domain.com
        .replace(/^\//, '')                  // Убираем начальный слеш
        .replace(/\/$/, '');                 // Убираем конечный слеш
      
      // Извлекаем slug (последнюю часть после raboty/)
      // Например: raboty/obemnoe-panno-zmeya-iz-gipsa => obemnoe-panno-zmeya-iz-gipsa
      const artworkSlug = fullArtworkPath.replace(/^raboty\//, '');
      
      console.log('   Сравнение путей:', { 
        fullArtworkPath, 
        artworkSlug, 
        cleanFullPath, 
        matchFull: fullArtworkPath === cleanFullPath,
        matchSlug: artworkSlug === cleanFullPath
      });
      
      // Проверяем оба варианта: полный путь и slug
      if (fullArtworkPath === cleanFullPath || artworkSlug === cleanFullPath) {
        console.log('✅ Найден по website_link!');
        return true;
      }
    }
    
    // ПРИОРИТЕТ 2: Fallback на старый формат id
    if (art.id === cleanFullPath) {
      console.log('✅ Найден по ID!');
      return true;
    }
    
    return false;
  });

  // Отслеживание просмотра товара
  useEffect(() => {
    if (artwork) {
      trackProductView(artwork.id, artwork.title);
    }
  }, [artwork]);
  
  // Generate SEO data for the artwork
  const seoData = artwork ? generateArtworkSEO(artwork) : null;
  const schemaData = artwork && seoData ? generateSchemaMarkup(artwork, seoData) : null;

  // Находим похожие работы (более гибкий алгоритм)
  const similarArtworks = artwork 
    ? allArtworks
        .filter(art => {
          if (art.id === artwork.id) return false;
          
          // Точное совпадение категории или техники
          if (art.category === artwork.category || art.medium === artwork.medium) return true;
          
          // Совпадение тегов (минимум 2 общих тега)
          if (artwork.tags && art.tags) {
            const commonTags = artwork.tags.filter(tag => art.tags?.includes(tag));
            if (commonTags.length >= 2) return true;
          }
          
          // Схожие категории (пейзажи)
          const landscapeCategories = ['пейзаж', 'морской пейзаж', 'природа'];
          if (landscapeCategories.includes(artwork.category) && landscapeCategories.includes(art.category)) return true;
          
          // Схожие категории (искусство)
          const artCategories = ['абстракция', 'сюрреализм', 'космос'];
          if (artCategories.includes(artwork.category) && artCategories.includes(art.category)) return true;
          
          return false;
        })
        .slice(0, 3)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Загрузка картины...</p>
        </div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Работа не найдена</h1>
          <Button asChild>
            <Link to="/raboty">Вернуться в галерею</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Get image source with PRIORITY on hosting
  const getImageSource = () => {
    if (!artwork.image) return art001;
    
    // ПРИОРИТЕТ 1: Если путь начинается с /images/ - загружаем НАПРЯМУЮ с хостинга
    if (artwork.image.startsWith('/images/')) {
      return `https://w-wagner.art${artwork.image}`;
    }
    
    // ПРИОРИТЕТ 2: Если путь из БД (/src/assets/) - конвертируем в хостинг
    if (artwork.image.startsWith('/src/assets/')) {
      const fileName = artwork.image.split('/').pop() || '';
      
      // Сначала проверяем локальный маппинг (для старых товаров)
      if (imageMap[artwork.image]) {
        return imageMap[artwork.image];
      }
      
      // Для новых товаров загружаем с хостинга
      return `https://w-wagner.art/images/${fileName}`;
    }
    
    // ПРИОРИТЕТ 3: Попытка по ID в маппинге
    if (imageMap[artwork.id]) {
      return imageMap[artwork.id];
    }
    
    // ПРИОРИТЕТ 4: Попытка по полному пути изображения
    if (imageMap[artwork.image]) {
      return imageMap[artwork.image];
    }
    
    // ПРИОРИТЕТ 5: Извлекаем имя файла и ищем в маппинге
    const fileName = artwork.image.split('/').pop() || '';
    if (fileName) {
      const baseName = fileName.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '');
      if (imageMap[baseName]) {
        return imageMap[baseName];
      }
      
      // Последняя попытка - загрузить напрямую с хостинга
      return `https://w-wagner.art/images/${fileName}`;
    }
    
    // Fallback
    return art001;
  };
  
  const imageSource = getImageSource();
  const isAvailable = artwork.status !== "продано";

  const contacts = {
    vk: "https://vk.com/im?sel=-214021218",
    whatsapp: "https://wa.me/79507818785?text=Добрый день. Заинтересовала картина " + artwork.title
  };

  const handleContactClick = (method: string) => {
    // Отслеживание кликов по соцсетям
    trackSocialClick(method === 'vk' ? 'VK' : 'WhatsApp', `product_${artwork.id}`, artwork.title);
    trackProductAction(artwork.id, `contact_${method}`, artwork.title);
    
    if (method === 'vk' || method === 'whatsapp') {
      window.open(contacts[method as keyof typeof contacts], '_blank');
      return;
    }
    
    navigator.clipboard.writeText(contacts[method as keyof typeof contacts] || "");
    toast({
      title: `Связаться через ${method}`,
      description: "Контактная информация скопирована в буфер обмена.",
    });
  };

  const handleShare = () => {
    trackProductAction(artwork.id, 'share', artwork.title);
    
    if (navigator.share) {
      navigator.share({
        title: artwork.title,
        text: artwork.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Ссылка скопирована",
        description: "Ссылка на картину скопирована в буфер обмена"
      });
    }
  };

  return (
    <>
      {seoData && (
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          ogImage={imageSource}
          canonicalUrl={artwork.website_link || `https://w-wagner.art/raboty/${artwork.id}`}
          schemaData={schemaData}
        />
      )}
      <StructuredData type="artwork" data={artwork} />
      
      <div className="min-h-screen bg-background py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navigation */}
          <div className="mb-8">
            <Button variant="ghost" asChild className="mb-4">
              <Link to="/raboty">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Вернуться в галерею
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
            {/* Image Section */}
            <div className="space-y-6">
              <div className="aspect-[4/5] rounded-lg overflow-hidden bg-card relative group">
                <img
                  src={imageSource}
                  alt={`Картина "${artwork.title}" художника Валерии Вагнер из Омска - ${artwork.category} ${artwork.medium} ${artwork.size} см`}
                  className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  width={600}
                  height={750}
                />
                <div className="absolute top-4 right-4 flex gap-2">
                  <Button size="sm" variant="secondary" onClick={handleShare}>
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="space-y-6">
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">{artwork.title}</h1>
                    <p className="text-xl text-muted-foreground capitalize">{artwork.category}</p>
                  </div>
                  <Badge variant={isAvailable ? "default" : "secondary"}>
                    {artwork.status}
                  </Badge>
                </div>
                
                <div className="text-2xl md:text-3xl font-bold text-primary mb-6">
                  {artwork.price.toLocaleString('ru-RU')} ₽
                </div>
              </div>

              {/* Specifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Характеристики картины</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Размер:</span>
                    <span className="font-medium">{artwork.size} см</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Материал:</span>
                    <span className="font-medium capitalize">{artwork.medium || artwork.technique || 'Не указано'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Основа:</span>
                    <span className="font-medium capitalize">{artwork.canvas || 'Не указано'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Формат:</span>
                    <span className="font-medium capitalize">{artwork.format || artwork.category || 'Не указано'}</span>
                  </div>
                  {artwork.year && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Год создания:</span>
                      <span className="font-medium">{artwork.year}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Description */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Описание</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {artwork.description}
                  </p>
                  {artwork.technique && (
                  <div className="mt-4 pt-6 border-t border-border/50">
                      <h4 className="font-medium mb-2">Техника:</h4>
                      <p className="text-sm text-muted-foreground">{artwork.technique}</p>
                    </div>
                  )}
                  {artwork.inspiration && (
                  <div className="mt-4 pt-6 border-t border-border/50">
                      <h4 className="font-medium mb-2">Вдохновение:</h4>
                      <p className="text-sm text-muted-foreground">{artwork.inspiration}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tags */}
              {artwork.tags && artwork.tags.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Теги</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {artwork.tags.map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {isAvailable && (
                  <Button 
                    onClick={() => {
                      trackProductAction(artwork.id, 'order_dialog_open', artwork.title);
                      setIsOrderDialogOpen(true);
                    }}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
                    size="lg"
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Заказать картину
                  </Button>
                )}
                
                 <div className="grid grid-cols-2 gap-3">
                   <Button 
                     variant="outline" 
                     onClick={() => handleContactClick('vk')}
                     className="w-full"
                   >
                     <MessageCircle className="w-4 h-4 mr-2" />
                     ВКонтакте
                   </Button>
                   <Button 
                     variant="outline"
                     onClick={() => handleContactClick('whatsapp')}
                     className="w-full"
                   >
                     <MessageCircle className="w-4 h-4 mr-2" />
                     WhatsApp
                   </Button>
                 </div>

                 {/* VK группа - добавлено под кнопками VK и WhatsApp */}
                 <Button 
                   variant="ghost" 
                   className="w-full"
                   onClick={() => {
                     trackSocialClick('VK группа', `product_${artwork.id}`, artwork.title);
                     window.open('https://vk.com/w_wagner_art', '_blank');
                   }}
                 >
                   <ExternalLink className="w-4 h-4 mr-2" />
                   Посмотреть в группе ВК
                 </Button>

                 {/* VK товар - если есть ссылка на конкретный товар */}
                 {artwork.vk_link && (
                   <Button 
                     variant="outline" 
                     className="w-full border-blue-500/30 text-blue-600 hover:bg-blue-500 hover:text-white"
                     onClick={() => {
                       trackProductAction(artwork.id, 'vk_product_visit', artwork.title);
                       trackSocialClick('VK товар', `product_${artwork.id}`, artwork.title);
                       window.open(artwork.vk_link, '_blank');
                     }}
                   >
                     <ExternalLink className="w-4 h-4 mr-2" />
                     Товар в VK магазине
                   </Button>
                 )}
              </div>
            </div>
          </div>

          {/* Similar Artworks */}
          {similarArtworks.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Похожие работы
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {similarArtworks.map((similarArt) => (
                  <Card key={similarArt.id} className="gallery-card group">
                    <Link to={`/raboty/${similarArt.id}`}>
                      <div className="aspect-[4/5] overflow-hidden relative">
                        <img
                          src={imageMap[similarArt.id] || imageMap[similarArt.image] || art001}
                          alt={`Картина "${similarArt.title}" - ${similarArt.category} ${similarArt.medium}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-1">{similarArt.title}</h3>
                        <p className="text-sm text-muted-foreground mb-2 capitalize">
                          {similarArt.category}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-primary">
                            {similarArt.price.toLocaleString('ru-RU')} ₽
                          </span>
                          <Badge variant={similarArt.status === "в наличии" ? "default" : "secondary"} className="text-xs">
                            {similarArt.status}
                          </Badge>
                        </div>
                      </CardContent>
                    </Link>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </div>
        
        <FloatingActions 
          currentPage="artwork-detail" 
          showOrderButton={isAvailable}
          artworkData={{
            title: artwork.title,
            id: artwork.id,
            price: artwork.price
          }}
        />
      </div>

        {/* Order Dialog */}
        <OrderDialog
          artworkTitle={artwork.title}
          artworkId={artwork.id}
          artworkPrice={artwork.price}
          open={isOrderDialogOpen}
          onOpenChange={setIsOrderDialogOpen}
        />
    </>
  );
};

export default ArtworkDetail;